import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { DotsLoader } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import type { IUser } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import { getInviteTeammatesListItems } from "app/components/SharingModal/components/InviteTeammatesList/helpers";
import type { IUserInfoExtended } from "app/components/SharingModal/types";
import { useAccountUsers } from "app/custom-hooks/useAccountUsers";
import { translate } from "app/filters/i18nFilter";
import { DangerZoneAction } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/settings/danger-zone-section/actions/DangerZoneAction";

import { useAllUsersSearch } from "./hooks/useAllUsersSearch";
import { PortalDropdownContainer, StyledAutocomplete } from "./OwnershiptTransferAction.styled";
import type { IDropdownPosition } from "./types";
import { transformUser } from "./utils";

interface IOwnershipTransferAction {
    onOwnerChange: (ownerId: number) => void;
}

export const OwnershipTransferAction = ({ onOwnerChange }: IOwnershipTransferAction) => {
    const isInternalUser = !!swSettings.user?.isInternalUser;
    const { data: accountUsers = [] } = useAccountUsers();
    const { searchUsers } = useAllUsersSearch();
    const autocompleteContainerRef = useRef<HTMLDivElement>(null);
    const [showAutocompleteField, setShowAutocompleteField] = useState<boolean>(false);
    const [dropdownPosition, setDropdownPosition] = useState<IDropdownPosition>({ top: 0, left: 0, width: 0 });
    const [selectedUser, setSelectedUser] = useState<IUserInfoExtended>();
    const originalOwner = useMemo(() => {
        const { id, username, firstname, lastname } = swSettings.user as IUser;
        return transformUser({
            Id: id,
            Email: username,
            FirstName: firstname,
            LastName: lastname,
            User: `${firstname} ${lastname}`,
        });
    }, []);

    const transformedAccountUsers = useMemo<IUserInfoExtended[]>(
        () => [...accountUsers.map(transformUser), originalOwner],
        [accountUsers, originalOwner],
    );

    useEffect(() => {
        const initialUser = transformedAccountUsers.find((user) => user.id === originalOwner.id);

        if (initialUser) {
            setSelectedUser(initialUser);
        }
    }, [transformedAccountUsers, originalOwner]);

    useEffect(() => {
        if (!showAutocompleteField || !autocompleteContainerRef.current) {
            return;
        }

        const updatePosition = () => {
            const rect = autocompleteContainerRef.current?.getBoundingClientRect();

            if (rect) {
                const { bottom, left, width } = rect;
                setDropdownPosition({
                    top: bottom,
                    left,
                    width,
                });
            }
        };

        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [showAutocompleteField]);

    const selectUser = (user: IUserInfoExtended) => async (): Promise<void> => {
        setSelectedUser(user);

        if (user.id) {
            onOwnerChange(user.id);
        }
    };

    const getAccountUsersData = async (query: string): Promise<JSX.Element[]> => {
        const lowercaseQuery = query?.toLowerCase();
        const filteredUsers = transformedAccountUsers.filter(
            (user) =>
                !lowercaseQuery ||
                user.text?.toLowerCase().includes(lowercaseQuery) ||
                user.email?.toLowerCase().includes(lowercaseQuery),
        );

        return getInviteTeammatesListItems(filteredUsers, selectUser);
    };

    const getAllUsersData = async (query: string): Promise<JSX.Element[]> => {
        const lowercaseQuery = query?.toLowerCase();
        const localMatches = transformedAccountUsers.filter(
            (user) =>
                lowercaseQuery &&
                (user.text?.toLowerCase().includes(lowercaseQuery) ||
                    user.email?.toLowerCase().includes(lowercaseQuery)),
        );

        const remoteResults = await searchUsers(query);
        const localIds = new Set(localMatches.map((user) => user.id));
        const deduplicatedRemote = remoteResults.filter((user) => !localIds.has(user.id));
        const combinedUsers = [...localMatches, ...deduplicatedRemote];
        return getInviteTeammatesListItems(combinedUsers, selectUser);
    };

    const getData = isInternalUser ? getAllUsersData : getAccountUsersData;
    const renderItems = ({ listItems }: { listItems: JSX.Element[] }) => {
        if (!listItems?.length) {
            return null;
        }

        const { top, left, width } = dropdownPosition;
        return createPortal(
            <PortalDropdownContainer
                data-react-aria-top-layer="true"
                onMouseDown={(event) => event.preventDefault()}
                top={top}
                left={left}
                width={width}
                data-automation="ownership-transfer-dropdown"
            >
                {listItems}
            </PortalDropdownContainer>,
            document.body,
        );
    };

    return (
        <DangerZoneAction
            text={translate("llmTracker.campaignQuery.edit.danger-zone.setting.transfer-ownership.text")}
            ctaText={translate("llmTracker.campaignQuery.edit.danger-zone.setting.transfer-ownership.cta")}
            actionName={"transfer-ownership"}
            onCtaClick={!showAutocompleteField ? () => setShowAutocompleteField(true) : undefined}
        >
            {showAutocompleteField ? (
                <Box
                    ref={autocompleteContainerRef}
                    data-automation="ownership-transfer-autocomplete"
                    width="100%"
                >
                    <StyledAutocomplete
                        selectedValue={selectedUser?.text}
                        getListItems={getData}
                        placeholder={translate(
                            "llmTracker.campaignQuery.edit.danger-zone.setting.transfer-ownership.autocomplete.placeholder",
                        )}
                        loadingComponent={<DotsLoader />}
                        searchIcon="share-new"
                        isFocused
                        shouldUpdateSelectedValue
                        disabled={!isInternalUser ? !transformedAccountUsers?.length : null}
                        renderItems={renderItems}
                    />
                </Box>
            ) : null}
        </DangerZoneAction>
    );
};
